import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, EMPTY, finalize, Subject, switchMap, takeUntil } from 'rxjs';
import { NgxPaginationModule } from 'ngx-pagination';
import { SettlementsService } from 'src/app/services/settlements.service';
import { SettlementContract } from 'src/app/models/settlement-contract.model';
import { AuthService } from 'src/app/shared/services/auth.service';

@Component({
  selector: 'app-contract-history',
  standalone: true,
  imports: [CommonModule, NgxPaginationModule],
  templateUrl: './contract-history.component.html',
  styleUrl: './contract-history.component.scss'
})
export class ContractHistoryComponent implements OnChanges {
  private readonly service = inject(SettlementsService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly requests = new Subject<number>();
  private readonly scopeChanges = new Subject<void>();
  @Input({ required: true }) settlementId!: number;
  @Input() editable = false;
  @Output() contractUploaded = new EventEmitter<void>();
  readonly contracts = signal<SettlementContract[]>([]);
  readonly loading = signal(false);
  readonly uploading = signal(false);
  readonly downloadingId = signal<number | null>(null);
  readonly error = signal('');
  readonly actionError = signal('');
  readonly success = signal('');
  readonly selectedFile = signal<File | null>(null);
  readonly page = signal(1);
  readonly total = signal(0);
  readonly pageSize = 20;

  get canUpload(): boolean { return this.editable && this.auth.canEditSettlements(); }
  get paginationId(): string { return `contract-history-${this.settlementId}-${this.editable}`; }

  constructor() {
    this.requests.pipe(
      switchMap(page => {
        this.error.set('');
        this.contracts.set([]);
        this.loading.set(true);
        return this.service.getSettlementContractHistory(this.settlementId, page, this.pageSize).pipe(
          takeUntil(this.scopeChanges),
          catchError(error => { this.error.set(this.errorText(error, 'consultar el historial')); return EMPTY; }),
          finalize(() => this.loading.set(false))
        );
      }), takeUntilDestroyed(this.destroyRef)
    ).subscribe(response => {
      this.contracts.set(response.data);
      this.total.set(response.meta.pagination.total_entries);
      this.page.set(response.meta.pagination.current_page);
    });
  }

  ngOnChanges(): void {
    this.scopeChanges.next();
    this.contracts.set([]);
    this.selectedFile.set(null);
    this.actionError.set('');
    this.success.set('');
    this.total.set(0);
    this.loadPage(1);
  }

  loadPage(page: number): void {
    if (!Number.isInteger(Number(this.settlementId)) || Number(this.settlementId) <= 0) return;
    this.page.set(page);
    this.requests.next(page);
  }

  selectFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!this.canUpload || this.uploading()) return;
    this.success.set('');
    this.actionError.set('');
    this.selectedFile.set(null);
    if (!file) return;
    if (!file.size || (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf'))) {
      this.actionError.set('Selecciona un archivo PDF válido.');
      return;
    }
    this.selectedFile.set(file);
  }

  upload(): void {
    const file = this.selectedFile();
    if (!file || !this.canUpload || this.uploading() || this.loading()) return;
    this.uploading.set(true);
    this.actionError.set('');
    this.success.set('');
    this.service.uploadSettlementContract(this.settlementId, file).pipe(
      takeUntil(this.scopeChanges), takeUntilDestroyed(this.destroyRef),
      finalize(() => this.uploading.set(false))
    ).subscribe({
      next: () => {
        this.selectedFile.set(null);
        this.success.set('Contrato guardado.');
        this.loadPage(1);
        this.contractUploaded.emit();
      },
      error: error => this.actionError.set(this.errorText(error, 'guardar el contrato'))
    });
  }

  download(contract: SettlementContract): void {
    if (this.downloadingId() !== null || this.uploading()) return;
    this.downloadingId.set(contract.id);
    this.actionError.set('');
    this.service.downloadSettlementContract(this.settlementId, contract.id).pipe(
      takeUntil(this.scopeChanges), takeUntilDestroyed(this.destroyRef),
      finalize(() => this.downloadingId.set(null))
    ).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        const name = (contract.filename || `contrato-v${contract.version}`).replace(/[\\/]/g, '_');
        anchor.download = name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
      error: error => this.actionError.set(this.errorText(error, 'descargar el contrato'))
    });
  }

  private errorText(error: unknown, action: string): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 403) return `No tienes permiso para ${action}.`;
      if (error.status === 401) return 'Tu sesión expiró. Inicia sesión nuevamente.';
    }
    return `No se pudo ${action}. Inténtalo nuevamente.`;
  }

}
